import { useEffect } from "react";

interface WakeLockSentinelLike {
  release: () => Promise<void>;
  addEventListener: (type: "release", fn: () => void) => void;
}

/**
 * Keeps the screen on while `on` (Live follows a game): a phone by the keyboard
 * must not go dark in the middle of a round. The browser lets go of it when the
 * page is hidden, so it is asked for again when the page comes back.
 */
export function useWakeLock(on: boolean) {
  useEffect(() => {
    const wake = (navigator as Navigator & { wakeLock?: { request: (type: "screen") => Promise<WakeLockSentinelLike> } }).wakeLock;
    if (!on || !wake) return;
    let lock: WakeLockSentinelLike | null = null;
    let done = false;
    const ask = () => {
      if (done || document.visibilityState !== "visible" || lock) return;
      wake
        .request("screen")
        .then((l) => {
          if (done) return void l.release().catch(() => undefined);
          lock = l;
          l.addEventListener("release", () => {
            lock = null;
          });
        })
        // Refused (battery saver, no permission): the screen just sleeps as usual.
        .catch(() => undefined);
    };
    ask();
    document.addEventListener("visibilitychange", ask);
    return () => {
      done = true;
      document.removeEventListener("visibilitychange", ask);
      void lock?.release().catch(() => undefined);
    };
  }, [on]);
}
